/**
 * The app's single i18next instance.
 *
 * Imported once, by main.jsx, before anything renders. Components use
 * react-i18next as normal:
 *
 *   const { t } = useTranslation('purchase');
 *   t('order.create')            // purchase namespace
 *   t('common:actions.save')     // another namespace
 *
 * What lives here, and nowhere else:
 *   - which language the interface is in, and <html lang dir> to match;
 *   - bilingual mode (bilingual.js), applied to every t() call;
 *   - lazy loading of translation files (lazyBackend.js).
 *
 * The language is chosen by context/LocaleContext.jsx from the person's and
 * the organization's settings. This module only applies it.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { createBilingualPostProcessor, POST_PROCESSOR } from './bilingual.js';
import { createLazyBackend } from './lazyBackend.js';
import {
  DEFAULT_LANGUAGE,
  EAGER_NAMESPACES,
  LANGUAGES,
  NAMESPACES,
  SOURCE_LANGUAGE,
  directionOf,
  normaliseLanguage,
  secondaryLanguageFor,
} from './languages.js';

/** Same key the pre-paint script in index.html reads. */
const STORAGE_KEY = 'nexerp.language';

const bilingual = { enabled: false, secondary: null };

/** The language this browser last showed — for the sign-in screen only. */
export function getStoredLanguage() {
  try {
    return normaliseLanguage(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function storeLanguage(language) {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Private mode: the server-side preference still applies after sign-in.
  }
}

/** Sets <html lang dir>, which every RTL rule in the stylesheets keys off. */
export function applyDocumentLanguage(language) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('lang', language);
  root.setAttribute('dir', directionOf(language));
}

const initialLanguage = getStoredLanguage() ?? DEFAULT_LANGUAGE;
applyDocumentLanguage(initialLanguage);

const backend = createLazyBackend(import.meta.glob('./locales/*/*.json'), () =>
  bilingual.enabled ? bilingual.secondary : null
);

export const i18nReady = i18next
  .use(backend)
  .use(createBilingualPostProcessor(() => bilingual))
  .use(initReactI18next)
  .init({
    lng: initialLanguage,
    fallbackLng: SOURCE_LANGUAGE,
    supportedLngs: LANGUAGES.map((language) => language.code),
    load: 'languageOnly',
    ns: [...EAGER_NAMESPACES],
    defaultNS: 'common',
    postProcess: [POST_PROCESSOR],
    interpolation: { escapeValue: false }, // React escapes already
    // An Arabic string left empty falls back to English instead of blanking.
    returnEmptyString: false,
    returnNull: false,
    showSupportNotice: false,
    react: {
      useSuspense: true,
      // Re-render when a namespace or a bilingual companion file arrives.
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added',
    },
  });
backend.attach(i18next);

/** The interface language currently shown. */
export const getCurrentLanguage = () => normaliseLanguage(i18next.language) ?? DEFAULT_LANGUAGE;
export const isRTL = () => directionOf(getCurrentLanguage()) === 'rtl';
export const isBilingual = () => bilingual.enabled;

/**
 * Switches the interface language with no reload: the new language's files
 * for every namespace already in use are fetched FIRST, then the switch
 * happens in one render, so nothing flashes back to a loading state.
 */
export async function setLanguage(language) {
  const next = normaliseLanguage(language) ?? DEFAULT_LANGUAGE;
  storeLanguage(next);
  bilingual.secondary = secondaryLanguageFor(next);
  if (bilingual.enabled) await i18next.loadLanguages(bilingual.secondary);
  if (next !== i18next.language) await i18next.changeLanguage(next);
  applyDocumentLanguage(next);
}

/**
 * Turns bilingual display on or off. Loads the secondary language for the
 * namespaces already in use, then asks every translated component to render
 * again (a language "change" to the same language is how i18next does that).
 */
export async function setBilingual(enabled) {
  const next = Boolean(enabled);
  bilingual.secondary = secondaryLanguageFor(getCurrentLanguage());
  if (next === bilingual.enabled) return;
  bilingual.enabled = next;
  // styles/rtl.css switches letter-spacing off and adds Arabic fonts under it.
  if (typeof document !== 'undefined') document.documentElement.dataset.bilingual = String(next);
  if (next) await i18next.loadLanguages(bilingual.secondary);
  await i18next.changeLanguage(i18next.language);
}

export { NAMESPACES };
export default i18next;
